export type RiskLevel =
  | "CRITICAL RISK (Directly malicious)"
  | "Extremely high risk"
  | "High risk"
  | "Medium risk"
  | "Low risk"
  | "Very low risk";

export interface AddressRiskResponse {
  riskScore: number | null;
  riskLevel: RiskLevel;
  numHops: number | null;
  maliciousAddressesFound: Array<{
    address: string;
    distance: number;
    name_tag: string | null;
    entity: string | null;
    category: string;
  }>;
  reasoning: string;
  attribution?: {
    name_tag: string;
    entity: string;
    category: string;
    address_role: string;
  } | null;
}

export interface SanctionsResponse {
  address: string;
  network: string | null;
  is_token_blacklisted: boolean;
  is_ofac_sanctioned: boolean;
  checked_at: string;
  attribution?: {
    name?: string;
    category?: string;
    role?: string;
    entity?: string | null;
    tags?: string[] | null;
    malicious?: boolean;
  } | null;
}

export interface RangeRiskConfig {
  baseUrl: string;
  riskBlockThreshold: number;
}

export type RangeRiskNetwork = "base";

export interface WalletScreeningResult {
  address: string;
  risk: AddressRiskResponse;
  sanctions: SanctionsResponse;
  blocked: boolean;
  reasons: string[];
}

const DEFAULT_PROXY_URL = "/api/proxy/range?path=";
const DEFAULT_NETWORK: RangeRiskNetwork = "base";

export function getRangeRiskConfig(): RangeRiskConfig {
  const thresholdRaw = Number(
    process.env.REACT_APP_RANGE_RISK_BLOCK_THRESHOLD || 6,
  );
  const riskBlockThreshold = Number.isFinite(thresholdRaw)
    ? Math.min(10, Math.max(1, thresholdRaw))
    : 6;
  return {
    baseUrl: DEFAULT_PROXY_URL,
    riskBlockThreshold,
  };
}

async function fetchRangeJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    cache: "no-store",
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Range API error (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export async function screenWallet(
  address: string,
  config: RangeRiskConfig,
  network: RangeRiskNetwork = DEFAULT_NETWORK,
): Promise<WalletScreeningResult> {
  const encodedAddress = encodeURIComponent(address);
  const riskUrl = `${config.baseUrl}/address?address=${encodedAddress}&network=${network}`;
  const sanctionsUrl = `${config.baseUrl}/sanctions/${encodedAddress}?include_details=false`;

  const [risk, sanctions] = await Promise.all([
    fetchRangeJson<AddressRiskResponse>(riskUrl),
    fetchRangeJson<SanctionsResponse>(sanctionsUrl),
  ]);

  const sanctionsBlocked =
    sanctions.is_token_blacklisted ||
    sanctions.is_ofac_sanctioned ||
    sanctions.attribution?.malicious === true;

  const riskScore = typeof risk.riskScore === "number" ? risk.riskScore : null;
  const riskBlocked =
    riskScore !== null && riskScore >= config.riskBlockThreshold;

  const reasons: string[] = [];
  if (sanctions.is_token_blacklisted) {
    reasons.push("Token issuer blacklist match.");
  }
  if (sanctions.is_ofac_sanctioned) {
    reasons.push("OFAC sanctions match.");
  }
  if (riskBlocked) {
    reasons.push(`Risk score ${riskScore} (${risk.riskLevel}).`);
  }
  if (!reasons.length && sanctions.attribution?.malicious) {
    reasons.push("Sanctions attribution indicates malicious activity.");
  }

  return {
    address,
    risk,
    sanctions,
    blocked: sanctionsBlocked || riskBlocked,
    reasons,
  };
}
